import React from 'react';
import { useAuth } from './AuthContext';
import { Permission } from '../types/Auth';

interface CanProps {
  /** Required permission(s). ANY-OF semantics. */
  permission: Permission | Permission[];
  children: React.ReactNode;
  /** Rendered when the staff member lacks the permission (default: nothing). */
  fallback?: React.ReactNode;
}

/**
 * Conditionally renders children based on the current staff member's effective
 * permissions. Use to hide write/destructive actions on read-only-visible pages
 * (e.g. a SUPPORT_AGENT who can view users but not edit them).
 */
const Can: React.FC<CanProps> = ({ permission, children, fallback = null }) => {
  const { hasAnyPermission } = useAuth();
  const required = Array.isArray(permission) ? permission : [permission];
  return <>{hasAnyPermission(required) ? children : fallback}</>;
};

export default Can;

/** Hook form for imperative checks (e.g. building a context-menu action list). */
export const useCan = () => {
  const { hasPermission, hasAnyPermission } = useAuth();
  return { can: hasPermission, canAny: hasAnyPermission };
};
